import { createApp } from "vue";
import { Vue3ProgressPlugin } from "@marcoschulte/vue3-progress";
import VueDOMPurifyHTML from "vue-dompurify-html";
import VueTippy from "vue-tippy";
import "tippy.js/dist/tippy.css"; // optional for styling
import * as Sentry from "@sentry/vue";
import { createPinia } from "pinia";

import App from "@/App.vue";

import { router } from "@/router";
import { store } from "@/store";
import { i18n } from "@/i18n";
import globalDerectives from "@/derectives";

import api from "@/plugins/api";
import userPrefs from "@/plugins/userPrefs";
import jsonToCsv from "@/plugins/jsonToCsv";

import { usePrimaryNavItems } from "@/composables";
import { preloadRuntimeData } from "@/bootstrap";

import {
  ClDataTable,
  ClFormLabel,
  ClFormInput,
  ClFormGroup,
  ClDataTableDeleteAction,
  ClDataTableEditAction,
  ClDataTableViewAction,
  ClButton,
  ClHeading,
  ClP,
  ClHr,
  ClPill,
  ClModal,
  ClSpinner,
  ClSidebar,
  ClSidebarNav,
  ClSidebarNavItemParent,
  ClSidebarNavItemChild,
  ClSidebarDropdown,
  ClSidebarDropdownSearch,
  ClSidebarDropdownMenuItem,
  ClDropdown,
  ClDropdownItem,
  ClTabs,
  ClTabsItem,
  ClBadge,
  ClBadgeLabel,
  ClEmailAttachment,
  ClSkeleton,
  ClSkeletonRow,
  ClFooter,
  ClHeaderDropdown,
  ClHeaderDropdownProduct,
  ClHeaderDropdownLink,
  ClToast,
  ClFormRadioGroup,
  ClFormTextarea,
  ClFormSelect,
  ClActiveFilter,
  ClStepper,
  ClToggle,
  ClTableWrapper,
  ClTable,
  ClThead,
  ClTbody,
  ClTr,
  ClTh,
  ClTd,
  ClTrSkeleton,
  ClThSorting,
  ClTablePageSize,
  ClTableBasicPagination,
  ClTablePaginationState,
  ClTableColumnVisibilityToggle,
  ClTableDataStateError,
  ClTableDataStateNoResults,
  ClTableDataStateNoData,
  ClInputCheckbox,
  ClInputSearch,
  ClInputRadio,
  ClAccordion,
  ClProgressBar,
  ClRadioButtons,
  ClRadioButton,
  ClMultiselect,
  ClTableActionButton,
  ClTableHeaderCell,
  ClTableColumnsConfigurationPopover,
  ClTableColumnsConfigurationPopoverItem,
  ClSelect,
  ClSelectItem,
  ClCallout,
  ClDataCard,
  ClDataCardFooterLoading,
  ClDataCardFooterBigNumber,
  ClDataCardFooterSmallNumber,
  ClRaisedTabSet,
  ClRaisedTab,
  ClChartCard,
  ClSearchableDropdown,
  ClBreadcrumbsV2,
  ClBreadcrumbsItem,
  // Icons
  ClIconEmptyBox,
  ClIconEmptyFolder,
  ClIconEmptyEmail,
  ClIconEmptyDashboard,
  ClIconEmptyDonutChart,
  ClIconEmptyUfo,
  ClIconEmptyGraph,
  // CbIcons
  CbIconAdd,
  CbIconAddProducts,
  CbIconAi,
  CbIconAiTag,
  CbIconAlignLeft,
  CbIconAntiSpamProtection,
  CbIconArrow,
  CbIconArrowDropdown,
  CbIconArrowUp,
  CbIconAttentionCritical,
  CbIconCheckLegacy,
  CbIconChevronDown,
  CbIconChevronLeft,
  CbIconChevronRight,
  CbIconChevronUp,
  CbIconClose,
  CbIconClose2,
  CbIconCloseLegacy,
  CbIconColumns,
  CbIconCompanies,
  CbIconContract,
  CbIconCopy,
  CbIconCustomers,
  CbIconCustomiseDisplay,
  CbIconDarkMode,
  CbIconDashboard,
  CbIconDate,
  CbIconDelete,
  CbIconDnsFiltering,
  CbIconDomain,
  CbIconDomainAllow,
  CbIconDoubleCheck,
  CbIconDownload,
  CbIconEdit,
  CbIconEmail,
  CbIconEmailArchiving,
  CbIconExternalLink,
  CbIconEyeHidden,
  CbIconFile,
  CbIconFilter,
  CbIconFilterLegacy,
  CbIconHistory,
  CbIconHome,
  CbIconImage,
  CbIconInformation,
  CbIconInProgress,
  CbIconInsight,
  CbIconLicense,
  CbIconLightMode,
  CbIconLock,
  CbIconLogOut,
  CbIconMailLock,
  CbIconMailSend,
  CbIconMenu,
  CbIconNotifications,
  CbIconOverview,
  CbIconPartnerPortal,
  CbIconPhishing,
  CbIconPlusLegacy,
  CbIconProducts,
  CbIconQuarantine,
  CbIconReorder,
  CbIconReorder2,
  CbIconReportManager,
  CbIconReporting,
  CbIconRestore,
  CbIconScheduler,
  CbIconSearch,
  CbIconSearchLegacy,
  CbIconSenderAllow,
  CbIconSettings,
  CbIconSort,
  CbIconSpy,
  CbIconSuccess,
  CbIconSupport,
  CbIconSystemMode,
  CbIconTable,
  CbIconTick,
  CbIconTraining,
  CbIconUpload,
  CbIconUser,
  CbIconView,
  CbIconWarning,
} from "@titanhq/frontend-component-library";

// Modules
import {
  PhishTitanModule,
  LicenceUsageModule,
  SettingsModule,
  PhishingSimulationsModule,
  ArcTitanModule,
  SecurityAwarnessTrainingModule,
  DashboarPlatFormdModule,
  ReportManagerModule,
} from "@/modules/index";

import "@/assets/main.css";
import "@marcoschulte/vue3-progress/style";

preloadRuntimeData();
const app = createApp(App);

router.afterEach(() => {
  window.Appcues.page();
});

Sentry.init({
  app,
  dsn: "https://192076b14b796bd099207bfbfc47ab17@o4507293832511488.ingest.us.sentry.io/4507538100715520",
  environment: "production",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const { registerNavItems } = usePrimaryNavItems();
const pinia = createPinia();

function registerModule(module) {
  module({ app, store, router, registerNavItems });
}

registerModule(LicenceUsageModule);
registerModule(SettingsModule);
registerModule(PhishingSimulationsModule);
registerModule(PhishTitanModule);
registerModule(ArcTitanModule);
registerModule(SecurityAwarnessTrainingModule);
registerModule(DashboarPlatFormdModule);
registerModule(ReportManagerModule);

app.use(i18n);
app.use(store);
app.use(pinia);
app.use(router);
app.use(VueDOMPurifyHTML);
app.use(userPrefs);
app.use(api);
app.use(jsonToCsv);
app.use(Vue3ProgressPlugin);

app.use(globalDerectives);

app.use(
  VueTippy,
  // optional
  {
    directive: "tippy", // => v-tippy
    component: "tippy", // => <tippy/>
    componentSingleton: "tippy-singleton", // => <tippy-singleton/>,
    defaultProps: {
      placement: "auto-end",
      allowHTML: true,
    }, // => Global default options * see all props
  },
);

app.component("ClDataTable", ClDataTable);
app.component("ClFormLabel", ClFormLabel);
app.component("ClFormTextarea", ClFormTextarea);
app.component("ClFormInput", ClFormInput);
app.component("ClFormGroup", ClFormGroup);
app.component("ClDataTableEditAction", ClDataTableEditAction);
app.component("ClDataTableDeleteAction", ClDataTableDeleteAction);
app.component("ClDataTableViewAction", ClDataTableViewAction);
app.component("ClButton", ClButton);
app.component("ClHeading", ClHeading);
app.component("ClP", ClP);
app.component("ClHr", ClHr);
app.component("ClPill", ClPill);
app.component("ClModal", ClModal);
app.component("ClSpinner", ClSpinner);
app.component("ClSidebar", ClSidebar);
app.component("ClSidebarNav", ClSidebarNav);
app.component("ClSidebarNavItemParent", ClSidebarNavItemParent);
app.component("ClSidebarNavItemChild", ClSidebarNavItemChild);
app.component("ClSidebarDropdown", ClSidebarDropdown);
app.component("ClSidebarDropdownSearch", ClSidebarDropdownSearch);
app.component("ClSidebarDropdownMenuItem", ClSidebarDropdownMenuItem);
app.component("ClDropdown", ClDropdown);
app.component("ClDropdownItem", ClDropdownItem);
app.component("ClTabs", ClTabs);
app.component("ClTabsItem", ClTabsItem);
app.component("ClBadge", ClBadge);
app.component("ClBadgeLabel", ClBadgeLabel);
app.component("ClEmailAttachment", ClEmailAttachment);
app.component("ClSkeleton", ClSkeleton);
app.component("ClSkeletonRow", ClSkeletonRow);
app.component("ClFooter", ClFooter);
app.component("ClHeaderDropdown", ClHeaderDropdown);
app.component("ClHeaderDropdownProduct", ClHeaderDropdownProduct);
app.component("ClHeaderDropdownLink", ClHeaderDropdownLink);
app.component("ClToast", ClToast);
app.component("ClFormRadioGroup", ClFormRadioGroup);
app.component("ClFormSelect", ClFormSelect);
app.component("ClActiveFilter", ClActiveFilter);
app.component("ClStepper", ClStepper);
app.component("ClToggle", ClToggle);
app.component("ClInputCheckbox", ClInputCheckbox);
app.component("ClInputSearch", ClInputSearch);
app.component("ClTableWrapper", ClTableWrapper);
app.component("ClTable", ClTable);
app.component("ClThead", ClThead);
app.component("ClTbody", ClTbody);
app.component("ClTr", ClTr);
app.component("ClTh", ClTh);
app.component("ClTd", ClTd);
app.component("ClTableDataStateError", ClTableDataStateError);
app.component("ClTableDataStateNoResults", ClTableDataStateNoResults);
app.component("ClTableDataStateNoData", ClTableDataStateNoData);
app.component("ClTrSkeleton", ClTrSkeleton);
app.component("ClThSorting", ClThSorting);
app.component("ClTablePageSize", ClTablePageSize);
app.component("ClTableBasicPagination", ClTableBasicPagination);
app.component("ClTablePaginationState", ClTablePaginationState);
app.component("ClInputRadio", ClInputRadio);
app.component("ClTableColumnVisibilityToggle", ClTableColumnVisibilityToggle);
app.component("ClAccordion", ClAccordion);
app.component("ClProgressBar", ClProgressBar);
app.component("ClDataCard", ClDataCard);
app.component("ClDataCardFooterLoading", ClDataCardFooterLoading);
app.component("ClDataCardFooterBigNumber", ClDataCardFooterBigNumber);
app.component("ClDataCardFooterSmallNumber", ClDataCardFooterSmallNumber);
app.component("ClRaisedTabSet", ClRaisedTabSet);
app.component("ClRaisedTab", ClRaisedTab);
app.component("ClChartCard", ClChartCard);
app.component("ClSearchableDropdown", ClSearchableDropdown);
app.component("ClBreadcrumbsV2", ClBreadcrumbsV2);
app.component("ClBreadcrumbsItem", ClBreadcrumbsItem);
app.component("ClRadioButtons", ClRadioButtons);
app.component("ClRadioButton", ClRadioButton);
app.component("ClTableHeaderCell", ClTableHeaderCell);
app.component("ClTableActionButton", ClTableActionButton);
app.component(
  "ClTableColumnsConfigurationPopover",
  ClTableColumnsConfigurationPopover,
);
app.component(
  "ClTableColumnsConfigurationPopoverItem",
  ClTableColumnsConfigurationPopoverItem,
);
app.component("ClSelect", ClSelect);
app.component("ClSelectItem", ClSelectItem);
app.component("ClCallout", ClCallout);
app.component("ClMultiselect", ClMultiselect);

// Icons
app.component("ClIconEmptyBox", ClIconEmptyBox);
app.component("ClIconEmptyFolder", ClIconEmptyFolder);
app.component("ClIconEmptyEmail", ClIconEmptyEmail);
app.component("ClIconEmptyDashboard", ClIconEmptyDashboard);
app.component("ClIconEmptyDonutChart", ClIconEmptyDonutChart);
app.component("ClIconEmptyUfo", ClIconEmptyUfo);
app.component("ClIconEmptyGraph", ClIconEmptyGraph);

// CbIcons
app.component("cb-icon-add", CbIconAdd);
app.component("cb-icon-add-products", CbIconAddProducts);
app.component("cb-icon-ai", CbIconAi);
app.component("cb-icon-ai-tag", CbIconAiTag);
app.component("cb-icon-align-left", CbIconAlignLeft);
app.component("cb-icon-anti-spam-protection", CbIconAntiSpamProtection);
app.component("cb-icon-arrow", CbIconArrow);
app.component("cb-icon-arrow-dropdown", CbIconArrowDropdown);
app.component("cb-icon-arrow-up", CbIconArrowUp);
app.component("cb-icon-attention-critical", CbIconAttentionCritical);
app.component("cb-icon-check-legacy", CbIconCheckLegacy);
app.component("cb-icon-chevron-down", CbIconChevronDown);
app.component("cb-icon-chevron-left", CbIconChevronLeft);
app.component("cb-icon-chevron-right", CbIconChevronRight);
app.component("cb-icon-chevron-up", CbIconChevronUp);
app.component("cb-icon-close", CbIconClose);
app.component("cb-icon-close-2", CbIconClose2);
app.component("cb-icon-close-legacy", CbIconCloseLegacy);
app.component("cb-icon-columns", CbIconColumns);
app.component("cb-icon-companies", CbIconCompanies);
app.component("cb-icon-contract", CbIconContract);
app.component("cb-icon-copy", CbIconCopy);
app.component("cb-icon-customers", CbIconCustomers);
app.component("cb-icon-customise-display", CbIconCustomiseDisplay);
app.component("cb-icon-dark-mode", CbIconDarkMode);
app.component("cb-icon-dashboard", CbIconDashboard);
app.component("cb-icon-date", CbIconDate);
app.component("cb-icon-delete", CbIconDelete);
app.component("cb-icon-dns-filtering", CbIconDnsFiltering);
app.component("cb-icon-domain", CbIconDomain);
app.component("cb-icon-domain-allow", CbIconDomainAllow);
app.component("cb-icon-double-check", CbIconDoubleCheck);
app.component("cb-icon-download", CbIconDownload);
app.component("cb-icon-edit", CbIconEdit);
app.component("cb-icon-email", CbIconEmail);
app.component("cb-icon-email-archiving", CbIconEmailArchiving);
app.component("cb-icon-external-link", CbIconExternalLink);
app.component("cb-icon-eye-hidden", CbIconEyeHidden);
app.component("cb-icon-file", CbIconFile);
app.component("cb-icon-filter", CbIconFilter);
app.component("cb-icon-filter-legacy", CbIconFilterLegacy);
app.component("cb-icon-history", CbIconHistory);
app.component("cb-icon-home", CbIconHome);
app.component("cb-icon-image", CbIconImage);
app.component("cb-icon-information", CbIconInformation);
app.component("cb-icon-in-progress", CbIconInProgress);
app.component("cb-icon-insight", CbIconInsight);
app.component("cb-icon-license", CbIconLicense);
app.component("cb-icon-light-mode", CbIconLightMode);
app.component("cb-icon-lock", CbIconLock);
app.component("cb-icon-log-out", CbIconLogOut);
app.component("cb-icon-mail-lock", CbIconMailLock);
app.component("cb-icon-mail-send", CbIconMailSend);
app.component("cb-icon-menu", CbIconMenu);
app.component("cb-icon-notifications", CbIconNotifications);
app.component("cb-icon-overview", CbIconOverview);
app.component("cb-icon-partner-portal", CbIconPartnerPortal);
app.component("cb-icon-phishing", CbIconPhishing);
app.component("cb-icon-plus-legacy", CbIconPlusLegacy);
app.component("cb-icon-products", CbIconProducts);
app.component("cb-icon-quarantine", CbIconQuarantine);
app.component("cb-icon-reorder", CbIconReorder);
app.component("cb-icon-reorder-2", CbIconReorder2);
app.component("cb-icon-report-manager", CbIconReportManager);
app.component("cb-icon-reporting", CbIconReporting);
app.component("cb-icon-restore", CbIconRestore);
app.component("cb-icon-scheduler", CbIconScheduler);
app.component("cb-icon-search", CbIconSearch);
app.component("cb-icon-search-legacy", CbIconSearchLegacy);
app.component("cb-icon-sender-allow", CbIconSenderAllow);
app.component("cb-icon-settings", CbIconSettings);
app.component("cb-icon-sort", CbIconSort);
app.component("cb-icon-spy", CbIconSpy);
app.component("cb-icon-success", CbIconSuccess);
app.component("cb-icon-support", CbIconSupport);
app.component("cb-icon-system-mode", CbIconSystemMode);
app.component("cb-icon-table", CbIconTable);
app.component("cb-icon-tick", CbIconTick);
app.component("cb-icon-training", CbIconTraining);
app.component("cb-icon-upload", CbIconUpload);
app.component("cb-icon-user", CbIconUser);
app.component("cb-icon-view", CbIconView);
app.component("cb-icon-warning", CbIconWarning);

app.mount("#app");

export default {};
